import { MetaProfile, UnlockState } from '../types';

const META_STORAGE_KEY = 'tenish.meta.v1';

interface RunSummary {
  victory: boolean;
  elitesDefeated: number;
  bossesDefeated: number;
  combosTriggered: number;
}

let memoryProfile: MetaProfile | null = null;

function defaultUnlocks(): UnlockState {
  return {
    maxRarity: 'common',
    eventPack: 1,
    cursePack: 1,
    abilityPack: 1,
  };
}

function createDefaultProfile(): MetaProfile {
  return {
    version: 1,
    stats: {
      runs: 0,
      wins: 0,
      elites: 0,
      bosses: 0,
      combos: 0,
    },
    unlocks: defaultUnlocks(),
  };
}

function cloneProfile(profile: MetaProfile): MetaProfile {
  return {
    version: 1,
    stats: { ...profile.stats },
    unlocks: { ...profile.unlocks },
  };
}

function getLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function sanitizeProfile(input: unknown): MetaProfile | null {
  if (!input || typeof input !== 'object') return null;
  const raw = input as Partial<MetaProfile>;
  if (!raw.stats || !raw.unlocks) return null;
  const stats = raw.stats as MetaProfile['stats'];
  const unlocks = raw.unlocks as UnlockState;
  if (
    typeof stats.runs !== 'number' ||
    typeof stats.wins !== 'number' ||
    typeof stats.elites !== 'number' ||
    typeof stats.bosses !== 'number' ||
    typeof stats.combos !== 'number'
  ) {
    return null;
  }
  if (
    !['common', 'uncommon', 'rare'].includes(unlocks.maxRarity) ||
    typeof unlocks.eventPack !== 'number' ||
    typeof unlocks.cursePack !== 'number' ||
    typeof unlocks.abilityPack !== 'number'
  ) {
    return null;
  }

  const profile: MetaProfile = {
    version: 1,
    stats: {
      runs: Math.max(0, Math.floor(stats.runs)),
      wins: Math.max(0, Math.floor(stats.wins)),
      elites: Math.max(0, Math.floor(stats.elites)),
      bosses: Math.max(0, Math.floor(stats.bosses)),
      combos: Math.max(0, Math.floor(stats.combos)),
    },
    unlocks: {
      maxRarity: unlocks.maxRarity,
      eventPack: Math.max(1, Math.floor(unlocks.eventPack)),
      cursePack: Math.max(1, Math.floor(unlocks.cursePack)),
      abilityPack: Math.max(1, Math.floor(unlocks.abilityPack)),
    },
  };

  profile.unlocks = computeUnlockState(profile);
  return profile;
}

export function loadMetaProfile(): MetaProfile {
  const storage = getLocalStorage();

  if (!storage) {
    if (!memoryProfile) {
      memoryProfile = createDefaultProfile();
    }
    return cloneProfile(memoryProfile);
  }

  const raw = storage.getItem(META_STORAGE_KEY);
  if (!raw) {
    const fresh = createDefaultProfile();
    storage.setItem(META_STORAGE_KEY, JSON.stringify(fresh));
    return cloneProfile(fresh);
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    const profile = sanitizeProfile(parsed);
    if (!profile) throw new Error('invalid profile');
    return profile;
  } catch {
    const fallback = createDefaultProfile();
    storage.setItem(META_STORAGE_KEY, JSON.stringify(fallback));
    return cloneProfile(fallback);
  }
}

export function saveMetaProfile(profile: MetaProfile): void {
  const sanitized = sanitizeProfile(profile) ?? createDefaultProfile();
  const storage = getLocalStorage();
  if (!storage) {
    memoryProfile = cloneProfile(sanitized);
    return;
  }
  storage.setItem(META_STORAGE_KEY, JSON.stringify(sanitized));
}

export function computeUnlockState(profile: MetaProfile): UnlockState {
  const unlocks = defaultUnlocks();

  if (profile.stats.elites >= 1) {
    unlocks.maxRarity = 'uncommon';
  }
  if (profile.stats.bosses >= 1) {
    unlocks.maxRarity = 'rare';
  }
  if (profile.stats.wins >= 3) {
    unlocks.eventPack = 2;
  }
  if (profile.stats.bosses >= 2) {
    unlocks.eventPack = 3;
  }
  if (profile.stats.elites >= 3) {
    unlocks.cursePack = 2;
  }
  if (profile.stats.combos >= 25) {
    unlocks.abilityPack = 2;
  }

  return unlocks;
}

export function updateMetaFromRunResult(profile: MetaProfile, runSummary: RunSummary): MetaProfile {
  const next: MetaProfile = cloneProfile(profile);
  next.stats.runs += 1;
  if (runSummary.victory) {
    next.stats.wins += 1;
  }
  next.stats.elites += Math.max(0, runSummary.elitesDefeated);
  next.stats.bosses += Math.max(0, runSummary.bossesDefeated);
  next.stats.combos += Math.max(0, runSummary.combosTriggered);
  next.unlocks = computeUnlockState(next);
  return next;
}

export function getNextUnlockHint(profile: MetaProfile): string {
  const p = profile.stats;
  if (p.elites < 1) return `Defeat 1 elite to unlock uncommon rewards (${p.elites}/1)`;
  if (p.bosses < 1) return `Defeat 1 boss to unlock rare rewards (${p.bosses}/1)`;
  if (p.wins < 3) return `Win 3 runs to unlock Event Pack 2 (${p.wins}/3)`;
  if (p.bosses < 2) return `Defeat 2 bosses to unlock Event Pack 3 (${p.bosses}/2)`;
  if (p.elites < 3) return `Defeat 3 elites to unlock Curse Pack 2 (${p.elites}/3)`;
  if (p.combos < 25) return `Trigger 25 combos to unlock Ability Pack 2 (${p.combos}/25)`;
  return 'All current milestone unlocks achieved.';
}
